/*
###################################################################
Fitinsur Open Insurance - Copyright 2019-2023 - All Rights Reserved
###################################################################
*/
/* eslint-disable no-unused-expressions */

import React, { useContext, useEffect, useRef, useState } from 'react';
import { Form, Formik } from 'formik';
import { useHistory, useLocation } from 'react-router-dom';
import Layout from 'components/Layout';
import Button, { ButtonContainer } from 'components/Button';
import Colors from 'constants/Colors';
import { ButtonContent } from './styles';
import { FitinsurAPI, MessageCenter } from 'utils';
import { GlobalAuthContext, GlobalLoaderContext } from 'store/context';
import ContentCard from 'components/ContentCard';
import FormContainer from 'components/FormContainer';
import FormSection from 'components/FormSection';
import Icon from 'components/Icon';
import Loader from 'components/Loader';
import TagsList from 'components/TagList';
import DragZoneUpload from 'components/DragZoneUpload'
import { camelCase, uniqueId, upperCase } from 'lodash';
import * as yup from 'yup';
import { AddButton } from './styles';
import HasPermission from 'components/HasPermission/index';
import ClaimsSummary from 'components/ClaimsSummary';
import { LostCancelButton } from 'components/index';

export default function ClaimsForm(props) {
  const loading = useContext(GlobalLoaderContext);
  const authContext = useContext(GlobalAuthContext);
  const history = useHistory();
  const fitinsur = new FitinsurAPI();
  const mc = new MessageCenter();
  const formikRef = useRef();
  const handleBack = () => history.goBack();

  const [isBound, setIsBound] = useState(false);
  const [questionnaire, setQuestionnaire] = useState([]);
  const [riskQuestionnaire, setRiskQuestionnaire] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [operation, setOperation] = useState({});
  const [removedRiskQuestionnaire, setRemovedRiskQuestionnaire] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [currentClient, setCurrentClient] = useState(null);
  const [summary, setSummary] = useState({});
  const [idProduct, setIdProduct] = useState(null);


  const SECTIONS = {
    SUMMARY: { name: 'SUMMARY', isMultiple: false, isRisk: false },
    BROKING: { name: 'BROKING', isMultiple: false, isRisk: false },
    FACILITY: { name: 'FACILITY', isMultiple: true, isRisk: true },
    ATTACHMENT: { name: 'ATTACHMENT', isMultiple: false, isRisk: false },
    ATTACHMENT_NOTES: { name: 'ATTACHMENT_NOTES', isMultiple: false, isRisk: false },
  };

  function handleSummary() {
    fitinsur.setAuthToken(authContext.token);
    fitinsur.requestHeaderClaims(props.match?.params?.idOperation)
      .then(response => response.json()).then(data => {
        setSummary({
          claimsReferenceNumber: data.ret_value.claimsReferenceNumber,
        });
      }).catch((error) => {
        mc.error(`There was an error while fetching your data: ${handleSummary.name}\n` + error);
        console.error('fitPostSummary - Error:', error);
      });
  };
  const getQuestionUniqueKey = (question, index) => {
    if (question.idQuestion === 112) return `${camelCase(question.dsQuestion)}`;

    const key = `${camelCase(question.dsQuestion)}${index != null ? `_${index}` : ""}_${question.idQuestion}`;
    formikRef.current?.setFieldValue(key, question.behaviorObject?.defaultValue || "");
    return key;
  };

  const getQuestionIsDisabled = (question, operation) => {
    if (operation?.idDomainStatus === 254 || operation?.idDomainStatus === 252) return true;

    let disabledFields = [59, 109, 124, 68]; // client, broker, leadingOffice
    if (props.match.params.idOperation) {
      disabledFields = [...disabledFields, 504]
    }
    return disabledFields && disabledFields.includes(question.idQuestion);
  }

  const getQuestionnaire = async () => {
    loading.start();

    fitinsur.setAuthToken(authContext.token);
    let response = await fitinsur.requestEditDocuments(props.match.params.idOperation)
      .then(x => x.json());

    setAttachments(response.ret_value?.map(v => ({ ...v, ...v.file })));

    fitinsur.setAuthToken(authContext.token);
    response = await fitinsur.requestProductsList().then(v => v.json())
    if (response.ret_code != 200) return;

    fitinsur.setAuthToken(authContext.token);
    if (props.match.params.idOperation) {
      response = await fitinsur.requestQuoteEdit(props.match.params.idProduct || 12, props.match.params.idOperation, 28).then(x => x.json());
    } else {
      response = await fitinsur.requestQuoteAdd(12, 28).then(x => x.json());
    }

    if (response?.ret_value?.operation?.idProduct) { 
      setIdProduct(response.ret_value.operation.idProduct)
    }

    let reportData = null;
    const retOperation = response.ret_value.operation;
    setOperation(retOperation);
    if (retOperation) {
      fitinsur.setAuthToken(authContext.token);
      let reportDataResponse = await fitinsur.requestGetPolicyReport(retOperation.idOperationParent).then(response => response.json())
      if (reportDataResponse.ret_code === 200) reportData = reportDataResponse.ret_value;
    }

    try {
      if (response.ret_code != 200) {
        mc.error(response.ret_msg_details);
        loading.stop();
        return;
      }

      const hideQuestions = [130, 116, 2919, 2933, 2927, 2911, 2640, 2950, 2951, 2942, 2908] // IDs of questions to exclude from the questionnaire
      let questionnaire = response.ret_value.operations
        .map(question => {
          if (question.idQuestion === 123) {
            return { ...question, idDomainQuestion: 43 }
          }

          return question;
        })
        .filter(question => !hideQuestions.includes(question.idQuestion))
        .sort((a, b) => a.sequenceQuestion - b.sequenceQuestion);

      let riskQuestionnaire = []

      if (reportData) {
        const facilityQuestion = questionnaire.find(v => v.idQuestion === 123);
        if (facilityQuestion?.answer) {
          try {
            facilityQuestion.answerJson = JSON.parse(facilityQuestion.answer);
          } catch (e) { }
        }
        const facilityOptions = reportData.facilities.map(facility => ({
          dsAnswerOption: facility.facility_name,
          idAnswerOption: facility.facility_id,
        }))
        setFacilities(reportData.facilities);
        questionnaire = questionnaire.map(v => v.idQuestion === facilityQuestion?.idQuestion ? ({ ...v, answerOptions: facilityOptions, idAnswerOption: facilityQuestion.answerJson?.idAnswerOption || null }) : v)
      }

      const facilityQuestionnaire = questionnaire
        .filter(question => upperCase(question?.behaviorObject?.sectionName) === 'FACILITY')
        .sort((a, b) => a.sequenceQuestion - b.sequenceQuestion)
        .map((question, index) => ({
          ...question,
          key: getQuestionUniqueKey(question, index),
          disabled: getQuestionIsDisabled(question, retOperation)
        }))

      const facilities = [uniqueId()];
      facilities.map(() => riskQuestionnaire.push(facilityQuestionnaire));

      questionnaire = questionnaire
        .filter(question => question.idQuestion !== 60)
        .filter(question => upperCase(question?.behaviorObject?.sectionName) !== 'FACILITY')

      setQuestionnaire(questionnaire
        .map((question, index) => ({
          ...question,
          key: getQuestionUniqueKey(question, index),
          disabled: getQuestionIsDisabled(question, retOperation)
        }))
      );

      setRiskQuestionnaire(riskQuestionnaire);

      loading.stop();
    } catch (e) {
      console.log(e);
      loading.stop();
    }
  }

  const buildValidation = () => {
    const validationSchema = {};

    for (const question of questionnaire) {
      if (!question.key) continue;

      validationSchema[question.key] = question.visibleQuestion && question.behaviorObject?.isRequired
        ? yup.string().required("* Required field")
        : yup.string().notRequired();
    }

    for (const [_, risk] of riskQuestionnaire.entries()) {
      for (const question of risk) {
        if (!question.key) continue;
        validationSchema[question.key] = question.visibleQuestion && question.behaviorObject?.isRequired ? yup.string().required("* Required field") : yup.string().notRequired();
      }
    }

    return yup.object().shape(validationSchema)
  }

  const removeAnswersDuplicateSession = (questions, index, partySectionName, values) => {
    questions = JSON.parse(JSON.stringify(questions));
    return questions.map(question => {
      const { defaultValue } = question.behaviorObject || {};
      if (partySectionName) {
        if (question.idQuestion === 282 || question.idQuestion === 284 || question.idQuestion === 286) {
          const partyAnswers = values[partySectionName];
          if (partyAnswers) {
            const typeOfPartyAnswer = partyAnswers.find(v => v.idQuestion === question.idQuestion);
            if (typeOfPartyAnswer) {
              question.answer = typeOfPartyAnswer.answer;
              question.answerJson = typeOfPartyAnswer.answerJson;
              question.idAnswerOption = typeOfPartyAnswer.idAnswerOption;
            }
          }
        } else {
          question.answer = defaultValue;
          question.answerJson = defaultValue;
        }
      } else if (question.idQuestion !== 60) {
        question.answer = defaultValue;
        question.answerJson = defaultValue;
      }

      if (question.idQuestion !== 282 && question.idQuestion !== 284 && question.idQuestion !== 286) {
        question.idAnswerOption = null;
      }

      question.idPerson = null;
      question.idQuestionnaireAnswers = null;
      question.key = null;
      question.key = getQuestionUniqueKey(question, index)
      return question;
    })
  }

  const renderAddAndRemoveButtons = (array, index, questions, setFunction, partySectionName, values) => {
    return (
      <>
        {index !== 0
          ? (
            <AddButton onClick={(e) => {
              e.preventDefault(); e.stopPropagation();
              setFunction(array.filter((v, i) => i !== index));
              setRemovedRiskQuestionnaire([
                ...removedRiskQuestionnaire,
                ...questions.map(question => ({ idQuestionnaireAnswers: question.idQuestionnaireAnswers })).filter(questions => questions && questions.idQuestionnaireAnswers)
              ])
            }}>
              <Icon name="trash" />
            </AddButton>
          ) : <></>
        }

        {array.length < facilities.length
          ? (
            <AddButton onClick={(e) => {
              e.preventDefault(); e.stopPropagation();
              setFunction([...array, removeAnswersDuplicateSession(questions, index, partySectionName, values)]);
            }}>
              <Icon name="plus" />
            </AddButton>
          )
          : <></>
        }
      </>
    )
  }

  const renderUploadButton = (values, options) => {
    return <DragZoneUpload multiple={true}
      options={options}
      documentTypeRequired={true}
      documentType={values.documentType}
      idOperation={operation?.idOperation}
      isDraft={!operation?.idOperation}
      onSuccess={(uploadedFiles) => setAttachments([...attachments, ...uploadedFiles])} />
  }

  const handleSelectDeal = (question, answer, filteredQuestionnaire, setFilteredQuestionnaire, setFieldValue) => {
    if (question.idQuestion === 504) {

      const { idOperation } = answer
      if (idOperation) {
        loading.start()
        fitinsur.setAuthToken(authContext.token);
        fitinsur.requestGetPolicyReport(idOperation)
          .then(response => response.json())
          .then(response => {
            if (response.ret_code !== 200) {
              mc.error(response.ret_msg_details);
              loading.stop();
              return;
            }

            setIdProduct(response.ret_value.idProduct)
            const clientQuestion = filteredQuestionnaire.find(v => v.idQuestion === 59);
            filteredQuestionnaire = filteredQuestionnaire.map(v => v.key === clientQuestion.key
              ? ({
                ...v,
                disabled: true,
                answer: JSON.stringify({
                  id: response.ret_value.client.idPerson,
                  personName: response.ret_value.client_name,
                  PERSON_FIRST_NAME: response.ret_value.client_name,
                  PERSON_LAST_NAME: response.ret_value.client_name,
                }),
                answerJson: {
                  id: response.ret_value.client.idPerson,
                  personName: response.ret_value.client_name,
                  PERSON_FIRST_NAME: response.ret_value.client_name,
                  PERSON_LAST_NAME: response.ret_value.client_name,
                }
              })
              : v)

            const brokerQuestion = filteredQuestionnaire.find(v => v.idQuestion === 109);
            filteredQuestionnaire = filteredQuestionnaire.map(v => v.key === brokerQuestion.key
              ? ({
                ...v,
                disabled: true,
                answer: JSON.stringify({
                  id: response.ret_value.broker?.idPerson,
                  personName: response.ret_value.broker_name,
                  PERSON_FIRST_NAME: response.ret_value.broker_name,
                  PERSON_LAST_NAME: response.ret_value.broker_name,
                }),
                answerJson: {
                  id: response.ret_value.broker?.idPerson,
                  personName: response.ret_value.broker_name,
                  PERSON_FIRST_NAME: response.ret_value.broker_name,
                  PERSON_LAST_NAME: response.ret_value.broker_name,
                }
              })
              : v
            )

            const leadingOfficeQuestion = filteredQuestionnaire.find(v => v.idQuestion === 124);
            filteredQuestionnaire = filteredQuestionnaire.map(v => v.key === leadingOfficeQuestion.key
              ? ({
                ...v,
                disabled: true,
                answer: response.ret_value.leadingOffice,
                answerJson: null,
                idAnswerOption: response.ret_value.id_person_leading_office
              })
              : v
            )

            const newRiskQuestionnaire = riskQuestionnaire.map(risk => {
              const facilityQuestion = risk.find(v => v.idQuestion === 123)

              const facilityOptions = response.ret_value.facilities.map(facility => ({
                dsAnswerOption: facility.facility_name,
                idAnswerOption: facility.facility_id,
              }))

              setFacilities(response.ret_value.facilities);
              return risk.map(v => v.key === facilityQuestion.key ? ({ ...v, answerOptions: facilityOptions }) : v)
            })

            filteredQuestionnaire = filteredQuestionnaire.map(v => v.key === question.key ? ({
              ...v,
              answer: JSON.stringify(answer),
              answerJson: answer,
            }) : v)

            setFilteredQuestionnaire(filteredQuestionnaire)
            setRiskQuestionnaire(newRiskQuestionnaire)
            setFieldValue(clientQuestion.key, response.ret_value.client_name);
            setFieldValue(brokerQuestion.key, response.ret_value.broker_name);
            setFieldValue(leadingOfficeQuestion.key, response.ret_value.leadingOffice);
            setFieldValue(question.key, answer.referenceNumber);
            loading.stop();
          })
          .catch((error) => {
            loading.stop()
            mc.error(`There was an error while fetching your data: ${handleSelectDeal.name}\n` + error);
          });
      }
    }
  }

  const handleChangeFacility = (question, answer, setFieldValue, index) => {
    if (question.idQuestion === 123) {
      const selectedFacility = facilities.find(facility => facility.facility_name === answer.dsAnswerOption);
      const newRiskQuestionnaire = riskQuestionnaire.map((risk, riskIndex) => {
        if (riskIndex === index) {
          const currencyQuestion = risk.find(v => v.idQuestion === 68)

          setFieldValue(question.key, answer);
          setFieldValue(currencyQuestion.key, { 
            idAnswerOption: null, 
            dsAnswerOption: selectedFacility.currency 
          });

          return risk.map(riskQuestion => {
            if (riskQuestion.key === currencyQuestion.key) {
              return ({
                ...riskQuestion,
                disabled: true,
                answer: selectedFacility.currency,
                answerJson: { 
                  idAnswerOption: null, 
                  dsAnswerOption: selectedFacility.currency
                },
                idAnswerOption: null
              })
            }

            if (riskQuestion.key === question.key) {
              return ({
                ...riskQuestion,
                answer: JSON.stringify(answer),
                answerJson: {
                  ...answer,
                  ...selectedFacility,
                },
              })
            }

            return riskQuestion
          })
        } else {
          return risk;
        }
      })

      setRiskQuestionnaire(newRiskQuestionnaire)
    }
  }

  const handleWaitingPeriod = async (idQuestion, answer, filteredQuestionnaire, setFilteredQuestionnaire, setFieldValue) => {
    try {
      if (idQuestion === 500) {
        const selectedFacility = filteredQuestionnaire?.find(question => question.idQuestion === 123);
        const endWaitingPeriodDateQuestion = filteredQuestionnaire.find(v => v.idQuestion === 503);
        const currentQuestion = filteredQuestionnaire.find(v => v.idQuestion === idQuestion);

        const currentFacility = facilities.find(facility => facility.facility_name === selectedFacility?.answerJson?.dsAnswerOption)

        filteredQuestionnaire = filteredQuestionnaire.map((question) => {
          const endWaitingPeriodDate = new Date();
          const defaultDate = answer;

          const waitingPeriod = isNaN(Number(currentFacility?.waiting_period)) ? 0 : Number(currentFacility?.waiting_period);
          endWaitingPeriodDate.setDate(defaultDate.getDate() + waitingPeriod)

          if (question.idQuestion === endWaitingPeriodDateQuestion.idQuestion) {
            setFieldValue(endWaitingPeriodDateQuestion.key, endWaitingPeriodDate.toLocaleDateString('en-GB'));
            return ({
              ...question,
              answer: endWaitingPeriodDate.toLocaleDateString('en-GB'),
              answerJson: endWaitingPeriodDate.toLocaleDateString('en-GB'),
            })
          }

          if (question.idQuestion === currentQuestion.idQuestion) {
            setFieldValue(currentQuestion.key, defaultDate.toLocaleDateString('en-GB'));
            return ({
              ...question,
              answer: defaultDate.toLocaleDateString('en-GB'),
              answerJson: defaultDate.toLocaleDateString('en-GB'),
            })
          }

          return question
        })

        console.log("filteredQuestionnaire", filteredQuestionnaire)
        setFilteredQuestionnaire(filteredQuestionnaire);
      }
    } catch (e) {
      console.log('error', e)
    }
  }

  const handleSaveEnquiry = async (values) => {
    values = JSON.parse(JSON.stringify(values));

    try {
      loading.start();

      let body = {
        create: !props.match.params.idOperation,
        idTemplate: 1,
        idProduct: 12,
        idOperation: props.match.params.idOperation,
        idOperationParent: null,
        operationsQuestionnaire: [],
        riskQuestionnaire: {
          groupings: []
        },
        deleteds: removedRiskQuestionnaire,
        attachments: []
      }

      for (const [key, section] of Object.entries(SECTIONS)) {
        if (section.isRisk) {
          riskQuestionnaire.map((riskAnswers, index) => {
            let sectionAnswers = values[`$ANSWERS_${section.name}_${index}`];
            if (sectionAnswers) {
              const facilityName = sectionAnswers.find(v => v.idQuestion === 123)
              if (facilityName) facilityName.idAnswerOption = null;

              const risk = { idOperation: body.idOperation, risks: sectionAnswers }
              body.riskQuestionnaire.groupings.push(risk);
            }
          })
        } else {
          let sectionAnswers = values[`$ANSWERS_${section.name}`];
          if (sectionAnswers) {
            body.operationsQuestionnaire = [...body.operationsQuestionnaire, ...sectionAnswers.filter(question => question.idQuestionnaire === 42)];
          }
        }
      }

      const operationParent = body.operationsQuestionnaire.find(question => question.idQuestion === 504);
      body.idOperationParent = operation?.idOperationParent || operationParent.answerJson.idOperation;

      //build attachment body
      const attachmentQuestion = questionnaire.find(question => question.idQuestion === 78);
      body.attachments = attachments.map(attachment => ({
        ...attachment,
        fileBase64: undefined,
        idQuestion: attachmentQuestion?.idQuestion,
        idQuestionnaire: attachmentQuestion?.idQuestionnaire,
        idDocument: attachment?.idDocument,
        idQuestionnaireAnswers: attachment?.idQuestionnaireAnswers,
      }))

      const facilityNames = [];
      body.riskQuestionnaire.groupings.map(group => {
        const facilityName = group.risks.find(v => v.idQuestion === 123);

        if (facilityName && facilityName.answer) {
          facilityName.answer = facilityName.answer.trim()
        }

        if (facilityName) facilityNames.push(facilityName.answer);
      })

      if (facilityNames.some(v => !v || !v.trim().length || !v.replace(/\s/g, '').length)) {
        mc.error(`Facility name should be filled`);
        loading.stop();
        return;
      }

      let nonRepeatedFacilityNames = [...new Set(facilityNames)];
      if (nonRepeatedFacilityNames.length < facilityNames.length) {
        mc.error("Facility name should be unique");
        loading.stop();
        return;
      }

      fitinsur.setAuthToken(authContext.token);
      fitinsur.requestCreateClaim(body)
        .then(response => response.json())
        .then(response => {
          if (response.ret_code !== 200) {
            loading.stop();
            mc.error(response.ret_msg_details);
            return;
          }
          history.push('/claims')
          loading.stop();
        })
        .catch((error) => {
          loading.stop()
          mc.error(`There was an error while fetching your data: ${handleSaveEnquiry.name}\n` + error);
        });
    } catch (d) {
      console.log(d);
      loading.stop();
      mc.error("An error happened, please try again.");
    }
  }

  const handleClaimComposition = (values) => {
    try {
      loading.start();
      let body = {
        idOperation: parseInt(props.match.params.idOperation),
        facilities,
        bolSaveLineSize: false,
      };

      fitinsur.setAuthToken(authContext.token);
      fitinsur.requestCreateClaimComposition(body)
        .then(x => x.json()).then(res => {
          if (res.ret_code !== 200) {
            mc.error(res.ret_msg_details);
            return;
          }

          history.push(`/claim-composition/${props.match.params.idOperation}`)
          loading.stop();
        }).catch(e => {
          loading.stop();
          mc.error(e.message);
          return;
        });
    } catch (err) {
      console.log('Error on lock Line Size', err);
    }
  };

  async function handleOperationInfo() {
    loading.start()
    fitinsur.setAuthToken(authContext.token);
    fitinsur.requestOperationInfo(props.match.params.idOperation)
      .then(response => response.json())
      .then(response => {
        setIsBound(response?.isBound);
        loading.stop();
      })
      .catch((error) => {
        mc.error(`There was an error while fetching your data: ${handleOperationInfo.name}\n` + error);
        loading.stop();
      });
  }

  useEffect(() => {
    getQuestionnaire();
    handleOperationInfo();
    handleSummary();
  }, []);

  return (
    <Layout
      idOperation={props.match?.params?.idOperation}
      title="CLAIMS" subtitle={summary.claimsReferenceNumber || ""}
    >
      <ClaimsSummary stage={1} idOperation={props.match?.params?.idOperation} />
      <ContentCard title="Summary" icon info={{ idOperation: props.match?.params?.idOperation || null, idProduct: props.location?.state?.idProduct || null }}>
        <Formik
          innerRef={formikRef}
          initialValues={{}}
          validationSchema={buildValidation()}
          enableReinitialize={true}
          onSubmit={values => handleSaveEnquiry(values)}
          render={({ values, setFieldValue }) => (
            <Form>
              <FormSection
                sectionName="SUMMARY"
                questionnaire={questionnaire}
                values={values}
                setFieldValue={setFieldValue}
                formikRef={formikRef}
                onCurrentClientChange={currentClient => setCurrentClient(currentClient)}
                onQuestionnaireChange={filteredQuestionnaire => setQuestionnaire(filteredQuestionnaire)}
                onQuestionChange={(question, answer, filteredQuestionnaire, setFilteredQuestionnaire, setFieldValue) => {
                  handleSelectDeal(question, answer, questionnaire, setQuestionnaire, setFieldValue);
                }}
                idProduct={idProduct}
              />

              <FormContainer title="BROKING">
                <FormSection
                  sectionName="BROKING"
                  questionnaire={questionnaire}
                  values={values}
                  setFieldValue={setFieldValue}
                  formikRef={formikRef}
                  externalFormCurrentClient={currentClient}
                  onCurrentClientChange={currentClient => setCurrentClient(currentClient)}
                  onQuestionnaireChange={filteredQuestionnaire => setQuestionnaire(filteredQuestionnaire)}
                  idProduct={idProduct}
                />
              </FormContainer>

              {riskQuestionnaire.map((questions, index) => (
                <FormContainer
                  show={true}
                  title={index === 0 ? "CLAIM DETAILS" : `FACILITY ${index}`}
                >
                  <FormSection
                    sectionName="FACILITY"
                    questionnaire={questions}
                    values={values}
                    setFieldValue={setFieldValue}
                    formikRef={formikRef}
                    externalFormCurrentClient={currentClient}
                    onCurrentClientChange={currentClient => setCurrentClient(currentClient)}
                    sectionIndex={index}
                    onQuestionChange={(question, answer, filteredQuestionnaire, setFilteredQuestionnaire, setFieldValue) => {
                      handleChangeFacility(question, answer, setFieldValue, index)
                      handleWaitingPeriod(question.idQuestion, answer, filteredQuestionnaire, setFilteredQuestionnaire, setFieldValue);
                    }}
                    idProduct={idProduct}
                  />
                  {renderAddAndRemoveButtons(riskQuestionnaire, index, questions, setRiskQuestionnaire, true)}
                </FormContainer>)
              )}

              <FormContainer show={true} title={"ATTACHMENTS"}>
                <FormSection
                  sectionName="ATTACHMENT"
                  questionnaire={questionnaire}
                  values={values}
                  setFieldValue={setFieldValue}
                  formikRef={formikRef}
                  externalFormCurrentClient={currentClient}
                  onQuestionnaireChange={filteredQuestionnaire => setQuestionnaire(filteredQuestionnaire)}
                  renderUploadButton={(options) => renderUploadButton(values, options)}
                  renderAttachments={() => <TagsList
                    disabled={false ? true : false}
                    tags={attachments}
                    removeTag={(currentAttachment) => setAttachments(attachments.filter(attachment => attachments.indexOf(attachment) !== currentAttachment))}
                  />}
                  idProduct={idProduct}
                />
              </FormContainer>

              {!operation?.idOperation && (
                <FormSection
                  sectionName="ATTACHMENT_NOTES"
                  questionnaire={questionnaire}
                  values={values}
                  setFieldValue={setFieldValue}
                  formikRef={formikRef}
                  externalFormCurrentClient={currentClient}
                  onQuestionnaireChange={filteredQuestionnaire => setQuestionnaire(filteredQuestionnaire)}
                  idProduct={idProduct}
                />
              )}
              <ButtonContainer>
                <Button forth onClick={() => handleBack()}> &lt; Back</Button>
                <HasPermission permission={!isBound}>
                  <ButtonContent>
                    {props.match?.params?.idOperation && <LostCancelButton idOperation={props.match?.params?.idOperation} screen={"claims"} />}
                    <Button
                      id="btnSave"
                      type="submit"
                      style={{ marginLeft: 15 }}
                      onClick={() => {
                        buildValidation().validate(values).catch((e) => {
                          console.log('validation error', e)
                          mc.error('Please fill in all required fields.')
                        })
                      }}
                      disabled={false}
                      primary
                    >
                      {operation?.idOperation ? "SAVE" : "ADD CLAIM"}
                    </Button>

                    {operation?.idDomainStatus === 249 &&
                      <Button
                        id="btnSubmitClaim"
                        type="button"
                        style={{ marginLeft: 15 }}
                        onClick={() => {
                          buildValidation().validate(values)
                            .then(() => handleClaimComposition(values))
                            .catch((e) => {
                              console.log('validation error', e)
                              mc.error('Please fill in all required fields.')
                            })
                        }}
                        disabled={false}
                        secundary
                      >
                        SUBMIT
                      </Button>
                    }
                  </ButtonContent>
                </HasPermission>
              </ButtonContainer>
            </Form>
          )}
        >
        </Formik>
        {loading.active && <Loader backdropColor={Colors.WHITE} fullScreen />}
      </ContentCard>
    </Layout >
  )
}
